import { useSelector } from 'react-redux'
import type { RootState } from '../store'

/**
 * Whether admin-only UI should be shown to the current user right now.
 *
 * Baseline: the user is a real admin (`state.auth.user.isAdmin`). Admins additionally get an "admin
 * mode" toggle in the profile menu (see UserMenu's "Admin Features" item) so they can preview the
 * app the way a normal user sees it; flipping it off hides admin extras.
 *
 * Components that previously gated rendering on `state.auth.user?.isAdmin` should use this instead
 * so the toggle can hide those admin extras. The toggle is a view preference, not an authorization
 * boundary — server-side checks still enforce real admin access.
 */
export function useAdminMode(): boolean {
  const isAdmin = useSelector((s: RootState) => s.auth.user?.isAdmin === true)
  const adminMode = useSelector((s: RootState) => s.auth.adminMode)
  return isAdmin && adminMode
}

/**
 * Whether the signed-in user is a real admin, ignoring the preview toggle.
 *
 * Use this to gate admin-only *pages* (like task detail) rather than `useAdminMode`. The preview
 * toggle is a view preference for admin extras on user-facing surfaces where the toggle is actually
 * reachable (the profile menu). It must NOT gate whole admin-only pages: those pages don't render
 * the toggle, so an admin with preview off would be locked out with no way to recover. Matches how
 * the /admin pages gate (on `user?.isAdmin`).
 */
export function useIsAdmin(): boolean {
  return useSelector((s: RootState) => s.auth.user?.isAdmin === true)
}

/**
 * Whether the signed-in user has been flagged for abuse by an admin. When true the app locks the user
 * out with a non-dismissable overlay on every page (see AbuseOverlay). The flag is set on the DB-backed
 * user record and surfaced by /api/auth-me; this is a client-side lockout, not an authorization
 * boundary — the server independently refuses to process a flagged user's GitHub App activity.
 */
export function useIsFlaggedForAbuse(): boolean {
  return useSelector((s: RootState) => s.auth.user?.flaggedForAbuse === true)
}
